import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  PayoutEventTypeEnum,
  AdminPayoutEventCreationOrUpdateRequest,
  AdminPayoutEvent,
  AdminProductListing,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import Icon from 'src/ui/atoms/icon';
import TimeInput from 'src/ui/atoms/time-input';
import Spacer from 'src/ui/atoms/spacer';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

interface AddPayoutModalProps {
  onSavePayout: () => void;
  onClose: () => void;
  products: AdminProductListing[];
  data?: AdminPayoutEvent;
  onSearchProduct: (val: string) => void;
}

interface AddPayoutFields {
  product: string;
  eventType: PayoutEventTypeEnum;
  paymentDate: Date;
}

export const Form = makeModalForm<AddPayoutFields>();

const AddPayoutModal: FunctionComponent<AddPayoutModalProps> = ({
  onSavePayout,
  onClose,
  products,
  data,
  onSearchProduct,
}) => {
  const { makeAuthenticatedApi, withApi } = useApiCall();

  const translate = useTranslateWithStringArgs();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback((fieldsValues: StateValues<AddPayoutFields>) => {
    const params: AdminPayoutEventCreationOrUpdateRequest = {
      productId: fieldsValues.product,
      payoutEventType: fieldsValues.eventType,
      paymentDate: fieldsValues.paymentDate,
    };

    withApi(async () => {
      if (data) {
        await api.adminPayoutEventsPartialUpdate({
          id: data.id,
          patchedAdminPayoutEventCreationOrUpdateRequest: params,
        });
      } else {
        await api.adminPayoutEventsCreate({ adminPayoutEventCreationOrUpdateRequest: params });
      }

      onSavePayout();
    });
  }, []);
  const isPhone = useIsMedia(DEVICES.phone);

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="studioPayoutEvents.addPayoutModal"
        onSubmit={(values: StateValues<AddPayoutFields>) => {
          onSubmit(values);
        }}
        initial={{
          product: data?.productId,
          eventType: data?.payoutEventType,
          paymentDate: data?.paymentDate,
        }}
      >
        <ModalHeader>
          <Translate name={`${data ? 'studioPayoutEvents.editPayout' : 'studioPayoutEvents.addPayout'}`} />
        </ModalHeader>
        <ModalContent>
          <>
            <Form.Group
              name="product"
              required={true}
              tooltip={
                <Tooltip
                  content={<Translate name="studioPayoutEvents.addPayoutModal.fields.product.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                >
                  <Icon name="question" circular={true} size="large" color="info" padded="none" />
                </Tooltip>
              }
            >
              <Form.Select
                options={products.map(({ id, name }) => ({
                  value: id,
                  label: name,
                }))}
                onInputChange={onSearchProduct}
              />
            </Form.Group>
            <Form.Group
              name="eventType"
              required={true}
              tooltip={
                <Tooltip
                  content={<Translate name="studioPayoutEvents.addPayoutModal.fields.eventType.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                >
                  <Icon name="question" circular={true} size="large" color="info" padded="none" />
                </Tooltip>
              }
            >
              <Form.Select
                options={Object.values(PayoutEventTypeEnum).map((eventType) => ({
                  value: eventType,
                  label: (
                    <Translate name={`studioPayoutEvents.payoutEventTypes.${snakeToCamelCaseWithNumbers(eventType)}`} />
                  ),
                }))}
              />
            </Form.Group>
            <Form.Group
              name="paymentDate"
              required={true}
              label={null}
              tooltip={
                <Tooltip
                  content={<Translate name="studioPayoutEvents.addPayoutModal.fields.paymentDate.tooltip" />}
                  position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                >
                  <Icon name="question" circular={true} size="large" color="info" padded="none" />
                </Tooltip>
              }
            >
              <Form.DatePicker
                minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                placeholder={translate('studioPayoutEvents.addPayoutModal.fields.paymentDate.label')}
              />
              <Spacer x={4} />
              <TimeInput value="23:59:59" disabled />
            </Form.Group>
          </>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.close" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'send',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddPayoutModal;
